import { useCallback, useMemo } from 'react'
import {
    DateRangeItemNames,
    DateRangeMasker,
    DateRanges,
    DateRangeValue,
} from './AnyDateRange.types'

const todayMilis = 1000 * 60 * 60 * 24

const startOfDay = (date: Date): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate())

const endOfDay = (date: Date): Date =>
    new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        23,
        59,
        59,
        999
    )

const startOfWeek = (date: Date): Date => {
    const day = startOfDay(date)
    return new Date(
        day.setDate(
            day.getDate() - day.getDay() + (day.getDay() === 0 ? -6 : 1)
        )
    )
}

/**
 * Pure resolver from a preset name to a concrete [start, end] range.
 * Used both by the picker (on selection) and by consumers that need to seed
 * an initial value that matches a preset. 'all_time' and 'custom' yield no
 * bounds (undefined), which callers should treat as "omit the filter".
 */
export const computeDateRange = (
    name: DateRangeItemNames,
    now: Date = new Date()
): DateRangeValue => {
    const today = startOfDay(now)
    switch (name) {
        case 'today':
            return { startDate: today, endDate: endOfDay(now) }
        case 'yesterday': {
            const yesterday = new Date(today.getTime() - todayMilis)
            return { startDate: yesterday, endDate: endOfDay(yesterday) }
        }
        case 'last_7_days':
            return {
                startDate: new Date(today.getTime() - todayMilis * 6),
                endDate: endOfDay(now),
            }
        case 'last_30_days':
            return {
                startDate: new Date(today.getTime() - todayMilis * 29),
                endDate: endOfDay(now),
            }
        case 'year_to_date':
            return {
                startDate: new Date(today.getFullYear(), 0, 1),
                endDate: endOfDay(now),
            }
        case 'last_12_months':
            return {
                startDate: new Date(
                    today.getFullYear() - 1,
                    today.getMonth(),
                    today.getDate()
                ),
                endDate: endOfDay(now),
            }
        case 'this_week': {
            const start = startOfWeek(now)
            return {
                startDate: start,
                endDate: endOfDay(new Date(start.getTime() + todayMilis * 6)),
            }
        }
        case 'last_week': {
            const start = new Date(startOfWeek(now).getTime() - todayMilis * 7)
            return {
                startDate: start,
                endDate: endOfDay(new Date(start.getTime() + todayMilis * 6)),
            }
        }
        case 'this_month':
            return {
                startDate: new Date(today.getFullYear(), today.getMonth(), 1),
                endDate: endOfDay(
                    new Date(today.getFullYear(), today.getMonth() + 1, 0)
                ),
            }
        case 'last_month':
            return {
                startDate: new Date(
                    today.getFullYear(),
                    today.getMonth() - 1,
                    1
                ),
                endDate: endOfDay(
                    new Date(today.getFullYear(), today.getMonth(), 0)
                ),
            }
        case 'all_time':
        case 'custom':
        default:
            return { startDate: undefined, endDate: undefined }
    }
}

export const useDates = (): [DateRanges, DateRangeMasker, DateRangeMasker] => {
    const maskStartDate = useCallback((date: Date) => {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate())
    }, [])

    const maskEndDate = useCallback((date: Date) => {
        return new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            23,
            59,
            59,
            999
        )
    }, [])

    const data = useMemo<DateRanges>(() => {
        const today = maskStartDate(new Date())
        const yesterday = maskStartDate(new Date(today.getTime() - todayMilis))
        const thisMonthStart = maskStartDate(
            new Date(today.getFullYear(), today.getMonth(), 1)
        )
        const thisMonthEnd = maskStartDate(
            new Date(
                new Date(
                    today.getFullYear(),
                    today.getMonth() + 1,
                    1
                ).getTime() - todayMilis
            )
        )
        const lastMonthStart = maskStartDate(
            new Date(today.getFullYear(), today.getMonth() - 1, 1)
        )
        const lastMonthEnd = maskStartDate(
            new Date(
                new Date(today.getFullYear(), today.getMonth(), 1).getTime() -
                    todayMilis
            )
        )
        const thisWeekStart = maskStartDate(
            new Date(
                new Date().setDate(
                    today.getDate() -
                        today.getDay() +
                        (today.getDay() === 0 ? -7 : 1)
                )
            )
        )
        const thisWeekEnd = maskStartDate(
            new Date(thisWeekStart.getTime() + todayMilis * 6)
        )
        const lastWeekStart = maskStartDate(
            new Date(thisWeekStart.getTime() - todayMilis * 7)
        )
        const lastWeekEnd = maskStartDate(
            new Date(lastWeekStart.getTime() + todayMilis * 6)
        )

        return {
            today,
            yesterday,
            thisMonthStart,
            thisMonthEnd,
            lastMonthStart,
            lastMonthEnd,
            thisWeekStart,
            thisWeekEnd,
            lastWeekStart,
            lastWeekEnd,
        }
    }, [maskStartDate])

    return [data, maskStartDate, maskEndDate]
}
